import { Mail, MapPin, Phone, Video } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { cn, initials } from '@/lib/utils'
import type { Channel, CollectionStatus, Delivery, MeetingResult, ModuleStatus, OfferStatus, OrderStatus, Priority, RenewalStatus, RequestStatus, Stage, TrialStatus, WorkStatus } from '../types'
import { useCrm } from '../ui'

const STAGE_CLASS: Record<Stage, string> = {
  lead: 'bg-violet/12 text-violet',
  demo: 'bg-info/12 text-info',
  proposal: 'bg-warning/15 text-warning-strong',
  customer: 'bg-success/12 text-success',
  lost: 'bg-muted text-muted-foreground',
}
export function StageBadge({ stage }: { stage: Stage }) {
  const { t } = useTranslation()
  return <Badge className={cn('rounded-md', STAGE_CLASS[stage])}>{t(`crm.stage.${stage}`)}</Badge>
}

export function DeliveryBadge({ delivery }: { delivery: Delivery }) {
  const { t } = useTranslation()
  if (delivery === 'none') return null
  const tone: Record<Delivery, BadgeTone> = { none: 'neutral', planned: 'info', installing: 'warning', live: 'success' }
  return <Badge tone={tone[delivery]}>{t(`crm.delivery.${delivery}`)}</Badge>
}

const RESULT_TONE: Record<MeetingResult, BadgeTone> = { reached: 'success', not_reached: 'danger', call_again: 'warning' }
export function ResultBadge({ result }: { result: MeetingResult }) {
  const { t } = useTranslation()
  return <Badge tone={RESULT_TONE[result]} className="rounded-md">{t(`crm.result.${result}`)}</Badge>
}

const CHANNEL_ICON = { visit: MapPin, phone: Phone, online: Video, email: Mail } satisfies Record<Channel, typeof Phone>
export function ChannelLabel({ channel, small }: { channel: Channel; small?: boolean }) {
  const { t } = useTranslation()
  const Icon = CHANNEL_ICON[channel]
  return (
    <span className={cn('inline-flex items-center', small ? 'gap-1 text-xs' : 'gap-1.5 text-sm')}>
      <span className={cn('flex items-center justify-center rounded-full bg-primary/10 text-primary', small ? 'size-4' : 'size-6')}>
        <Icon className={small ? 'size-2.5' : 'size-3.5'} aria-hidden />
      </span>
      {t(`crm.channel.${channel}`)}
    </span>
  )
}

export function OfferStatusBadge({ status }: { status: OfferStatus }) {
  const { t } = useTranslation()
  const tone: Record<OfferStatus, BadgeTone> = { draft: 'neutral', sent: 'info', accepted: 'success', rejected: 'danger' }
  return <Badge tone={tone[status]}>{t(`crm.offerStatus.${status}`)}</Badge>
}

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  const { t } = useTranslation()
  const tone: Record<OrderStatus, BadgeTone> = { pending: 'warning', approved: 'success', rejected: 'danger' }
  return <Badge tone={tone[status]}>{t(`crm.orderStatus.${status}`)}</Badge>
}

export function TrialStatusBadge({ status }: { status: TrialStatus }) {
  const { t } = useTranslation()
  const tone: Record<TrialStatus, BadgeTone> = { active: 'info', expired: 'danger', converted: 'success', cancelled: 'neutral' }
  return <Badge tone={tone[status]}>{t(`crm.trialStatus.${status}`)}</Badge>
}

export function RenewalStatusBadge({ status }: { status: RenewalStatus }) {
  const { t } = useTranslation()
  const tone: Record<RenewalStatus, BadgeTone> = { upcoming: 'info', contacted: 'warning', renewed: 'success', cancelled: 'neutral' }
  return <Badge tone={tone[status]}>{t(`crm.renewalStatus.${status}`)}</Badge>
}

export function ModuleStatusBadge({ status }: { status: ModuleStatus }) {
  const { t } = useTranslation()
  const tone: Record<ModuleStatus, BadgeTone> = { active: 'success', trial: 'info', planned: 'warning', cancelled: 'neutral' }
  return <Badge tone={tone[status]}>{t(`crm.moduleStatus.${status}`)}</Badge>
}

export function WorkStatusBadge({ status }: { status: WorkStatus }) {
  const { t } = useTranslation()
  const tone: Record<WorkStatus, BadgeTone> = { planned: 'info', in_progress: 'warning', done: 'success', cancelled: 'neutral' }
  return <Badge tone={tone[status]}>{t(`crm.workStatus.${status}`)}</Badge>
}

export function RequestStatusBadge({ status }: { status: RequestStatus }) {
  const { t } = useTranslation()
  const tone: Record<RequestStatus, BadgeTone> = { open: 'danger', in_progress: 'warning', resolved: 'success' }
  return <Badge tone={tone[status]}>{t(`crm.requestStatus.${status}`)}</Badge>
}

export function PriorityBadge({ priority }: { priority: Priority }) {
  const { t } = useTranslation()
  const tone: Record<Priority, BadgeTone> = { low: 'neutral', medium: 'warning', high: 'danger' }
  return <Badge tone={tone[priority]} className="rounded-md">{t(`crm.priority.${priority}`)}</Badge>
}

export function CollectionStatusBadge({ status }: { status: CollectionStatus }) {
  const { t } = useTranslation()
  const tone: Record<CollectionStatus, BadgeTone> = { pending: 'info', partial: 'warning', paid: 'success', overdue: 'danger' }
  return <Badge tone={tone[status]}>{t(`crm.collectionStatus.${status}`)}</Badge>
}

const AVATAR_TONES = ['bg-primary/15 text-primary', 'bg-success/15 text-success', 'bg-violet/15 text-violet', 'bg-brand-orange/15 text-brand-orange', 'bg-info/15 text-info']
/** Müşteri logosu yoksa adının baş harfleri; renk ada göre sabit. */
export function CustomerAvatar({ name, className }: { name: string; className?: string }) {
  const tone = AVATAR_TONES[[...name].reduce((s, ch) => s + ch.charCodeAt(0), 0) % AVATAR_TONES.length]
  return <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold', tone, className)}>{initials(name)}</span>
}

export function CustomerPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation()
  const { customers } = useCrm()
  return <Select className="w-56" value={value} onChange={onChange} placeholder={t('crm.pickCustomer')} aria-label={t('crm.pickCustomer')} options={customers.map((c) => ({ value: c.id, label: c.name }))} />
}
